import {
  DateCalendar as MuiDateCalendar,
  PickersDay,
  PickersDayProps,
  type DateCalendarProps as MuiDateCalendarProps,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import type { Dayjs } from 'dayjs';

type DateCalendarPropsWithDate = Omit<
  MuiDateCalendarProps<Dayjs>,
  'value' | 'onChange' | 'defaultValue' | 'minDate' | 'maxDate'
> & {
  id?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  defaultValue?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
};

const adapter = new AdapterDayjs();
export const DateCalendar = ({
  disabled = false,
  value,
  onChange,
  defaultValue,
  minDate,
  maxDate,
  ...rest
}: DateCalendarPropsWithDate) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDateCalendar
        disabled={disabled}
        value={value ? adapter.date(value.toISOString()) : null}
        onChange={(newValue: Dayjs | null) => {
          onChange?.(
            newValue ? adapter.toJsDate(newValue.startOf('day')) : null,
          );
        }}
        defaultValue={
          defaultValue ? adapter.date(defaultValue.toISOString()) : null
        }
        minDate={
          minDate
            ? (adapter.date(minDate.toISOString()) ?? undefined)
            : undefined
        }
        maxDate={
          maxDate
            ? (adapter.date(maxDate.toISOString()) ?? undefined)
            : undefined
        }
        {...rest}
      />
    </LocalizationProvider>
  );
};

export const HighlightedCalendarDay = <T extends Dayjs>(
  props: PickersDayProps<T> & {
    highlightedDays?: number[];
    disableNonHighlightedDays?: boolean;
  },
) => {
  const {
    day,
    outsideCurrentMonth,
    highlightedDays = [],
    disableNonHighlightedDays,
    ...other
  } = props;

  const isSelected =
    !props.outsideCurrentMonth &&
    highlightedDays.indexOf(props.day.date()) >= 0;

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      disabled={disableNonHighlightedDays && !isSelected}
      sx={theme => ({
        background: isSelected
          ? theme.palette.primary.background
          : 'transparent',
      })}
    />
  );
};
